import App from 'pages/_app'
import { render } from '@testing-library/react'
import {
  getDetailedLesson, getFocusSeshLogs, getFocusSeshPresets, getInitialDailyBeats,
  getInitialLessons, getNextDailyBeatItem,
  getUser,
  postUpdateLessonProgress,
} from '../requests/studentRequests'
import {
  getAllClassrooms,
  getContentfulCourseId, getInitialDailyBeatsForClassroom, getInitialLessonsForClassroom,
  getMostRecentFocusSeshDataForEachStudentInClassroom, getStudentsAndClassroomDataFromClassroomId,
  getStudentsForSchool,
} from '../requests/teacherRequests'


export const isTestEnv = () => {
  return process.env.NODE_ENV === 'test'
}

export const renderInApp = (component, pageProps={}) => {
  return render(<App Component={component} pageProps={pageProps}/>)
}

/**
 * Builds a matcher for text that is split across sibling elements, which is how the lesson
 * player renders card text: one span per word, so that narration can reveal them one at a
 * time. A plain string matcher only ever sees a single word and fails on any phrase.
 *
 * Matches the innermost element whose combined text contains the phrase, so a hit isn't also
 * reported for every ancestor.
 *
 * Usage: await findByText(brokenUpText('Research shows'))
 */
export const brokenUpText = (phrase) => {
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim()
  const target = normalize(phrase)
  const contains = (element) => normalize(element?.textContent).includes(target)

  return (_content, element) => {
    if (!contains(element)) return false
    return !Array.from(element?.children || []).some(contains)
  }
}

/**
 * Next's router exposes route changes through `router.events`, an event emitter. Components
 * that subscribe to it — the lesson player leaves fullscreen on 'routeChangeStart' — throw
 * without one, so the mocked router needs a working emitter rather than a bare object.
 *
 * The emitter is returned by mockRouterPath, so a test can simulate navigating away:
 *   const { events } = mockRouterPath('/portal/lesson/1')
 *   events.emit('routeChangeStart')
 */
export const createMockRouterEvents = () => {
  const handlersByEvent = {}

  return {
    on: (event, handler) => {
      handlersByEvent[event] = [...(handlersByEvent[event] || []), handler]
    },
    off: (event, handler) => {
      handlersByEvent[event] = (handlersByEvent[event] || []).filter((h) => h !== handler)
    },
    emit: (event, ...args) => {
      (handlersByEvent[event] || []).forEach((handler) => handler(...args))
    },
  }
}

export const mockRouterPath = (pathname, query = {}, backMock = jest.fn(), pushMock = jest.fn(), replaceMock = jest.fn()) => {
  const events = createMockRouterEvents()
  const useRouter = jest.spyOn(require('next/router'), 'useRouter')
  useRouter.mockImplementation(() => ({
    asPath: pathname,
    pathname,
    query,
    back: backMock,
    push: pushMock,
    replace: replaceMock,
    events,
  }))
  return { events }
}

export const mockRequests = (
  {
    testLessons = require('tests/testData/lessons/lessonOneStartedNoneComplete.json'),
    testDailyBeats = require('tests/testData/dailyBeats/noCompleteDailyBeats.json'),
    testNextDailyBeat = require('tests/testData/nextDailyBeatItem/nextDailyBeatCard.json'),
    testUser = require('tests/testData/users/testUserResponse.json'),
    testDetailedLesson = require('tests/testData/lessonDetails/lessonDetailsCardTextAndImage.json'),
    testLessonSummaries = require('tests/testData/lessonSummaries/testLessonSummaries.json'),
    testUserLessonsPost = require('tests/testData/userLessonsPost/responseWithoutAnswerItem.json'),
    focusSeshPresets = require('tests/testData/focusSeshPresets/populatedPresets.json'),
    focusSeshLogs = [],
  }) => {
  getInitialLessons.mockImplementation(async () => testLessons)
  getInitialDailyBeats.mockImplementation(async () => testDailyBeats)
  getNextDailyBeatItem.mockImplementation(async () => testNextDailyBeat)
  getUser.mockImplementation(async () => testUser)
  getDetailedLesson.mockImplementation(async () => testDetailedLesson)
  postUpdateLessonProgress.mockImplementation(async () => testUserLessonsPost)
  getContentfulCourseId.mockImplementation(async () => 'courseId')
  getFocusSeshPresets.mockImplementation(async () => focusSeshPresets)
  getFocusSeshLogs.mockImplementation(async () => focusSeshLogs)
}

export const mockTeacherRequests = (
  {
    testClassrooms = require('tests/testData/classrooms/testClassrooms.json'),
    testClassroom = null,
    testStudents = require('tests/testData/students/testStudentsResponse.json'),
    testTeacher = require('tests/testData/users/testUserResponseTeacher-Approved.json'),
    testLessons = require('tests/testData/lessons/lessonOneStartedNoneComplete.json'),
    testInitialLessonsForClassroomMode = require('tests/testData/lesssonsForClassroomMode/initialLessonsForClassroomMode.json'),
    testDailyBeats = require('tests/testData/dailyBeats/noCompleteDailyBeats.json'),
    testInitialDailyBeatsForClassroomMode = require('tests/testData/dailyBeatsForClassroomMode/initialDailyBeatsForClassroomMode.json'),
    testNextDailyBeat = require('tests/testData/nextDailyBeatItem/nextDailyBeatCard.json'),
    testDetailedLesson = require('tests/testData/lessonDetails/lessonDetailsCardTextAndImage.json'),
    testUserLessonsPost = require('tests/testData/userLessonsPost/responseWithoutAnswerItem.json'),
    mostRecentFocusSeshDataForEachStudent = require('tests/testData/mostRecentFocusSeshDataForEachStudent/classroomId-5446-allStudentsInactive.json')
  }) => {
  getAllClassrooms.mockImplementation(async () => testClassrooms)
  getStudentsAndClassroomDataFromClassroomId.mockImplementation(async () => ({ classroom: testClassroom, students: testStudents }))
  getUser.mockImplementation(async () => testTeacher)
  getStudentsForSchool.mockImplementation(async () => testStudents)
  getContentfulCourseId.mockImplementation(async () => 'courseId')

  getInitialLessons.mockImplementation(async () => testLessons)
  getInitialDailyBeats.mockImplementation(async () => testDailyBeats)
  getNextDailyBeatItem.mockImplementation(async () => testNextDailyBeat)
  getDetailedLesson.mockImplementation(async () => testDetailedLesson)
  postUpdateLessonProgress.mockImplementation(async () => testUserLessonsPost)
  getMostRecentFocusSeshDataForEachStudentInClassroom.mockImplementation(async () => mostRecentFocusSeshDataForEachStudent)
  getInitialLessonsForClassroom.mockImplementation(async () => testInitialLessonsForClassroomMode)
  getInitialDailyBeatsForClassroom.mockImplementation(async () => testInitialDailyBeatsForClassroomMode)
}
